import { useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Modal } from "react-bootstrap";
import { DataContext } from "../DataContext";
import { Button, VerifyEmail, AccountTemplate, Wrapper, PageTemplate, HeaderTemplate, NoOrdersYet, ButtonLink } from "../styles";
import { changePassword } from "../api/user";
import { PasswordTemplate } from "../templates/PasswordTemplate";
import { ProductsTemplate } from "../templates/ProductsTemplate";
import { ViewReturnsTemplate } from "../templates/ViewReturnsTemplate";
import { convertDate, hasReturnWindowExpired } from "../utils";
import { useNavigate } from "react-router-dom";
import { updateStatus } from "../api/order";
import toast from "react-hot-toast";

const Customer = () => {
  const { user, logout, isLoading, isAuthenticated } = useAuth0();
  const data = useContext(DataContext);

  const { customer } = data;

  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [modalTemplate, setModalTemplate] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showReturnsModal, setShowReturnsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  // component mount
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      data.getCustomer();
    }
  }, []);

  const handlePasswordChange = () => {
    changePassword(user);
    setModalTemplate("password");
    handleShowModal();
  };

  const handleViewProducts = (order) => {
    setCurrentOrder(order);
    setModalTemplate("products");
    handleShowModal();
  };

  const handleViewReturns = (order) => {
    setCurrentOrder(order);
    setShowReturnsModal(true);
  };

  const handleLogout = () => {
    data.clearCartState();
    sessionStorage.removeItem("login-time");
    logout();
  };

  const removeDash = (status) => {
    return status.replace(/-/g, " ");
  };

  const handleShowCancelModal = (order) => {
    setCurrentOrder(order);
    setShowCancelModal(true);
  };

  const handleOrderCancellation = async () => {
    try {
      await updateStatus({
        orderNumber: currentOrder.orderNumber,
        status: "cancelled",
      });
      setShowCancelModal(false);
      // only get customer if logged in
      if (isAuthenticated) {
        data.getCustomer();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };

  if (!customer || !user) return null;

  return user.email_verified ? (
    <>
      <HeaderTemplate className="narrow">
        <div className="header flex-header">
          {customer.orders.length > 0 && (
            <>
              <div className="title">
                <h1>Account</h1>
              </div>
              <div className="breadcrumb">
                <ButtonLink to="/designs" className="small clear">
                  Browse Designs
                </ButtonLink>
              </div>
            </>
          )}
        </div>
      </HeaderTemplate>
      <AccountTemplate className="account-template narrow">
        <div className="account-info box-padding">
          <div>
            <div className="name">{customer.customer.name}</div>
            <div className="email">{customer.customer.email}</div>
          </div>
          {!isLoading && isAuthenticated && (
            <div className="account-buttons">
              <Button className="white small" onClick={handlePasswordChange}>
                Change Password
              </Button>
              <Button className="logout white small" onClick={handleLogout}>
                Log Out
              </Button>
            </div>
          )}
        </div>
        <div className="order-history">
          {customer.orders.length > 0 ? (
            customer.orders.map((order) => {
              const availableProductsForReturn = () => {
                if (order.returns.length === 0) return true;
                return order.returns[order.returns.length - 1].products.find((product) => {
                  return product.returnType === "Keep";
                });
              };

              const returnWindowExpired = hasReturnWindowExpired(order.updatedAt);

              const showCreateReturnButton =
                !returnWindowExpired && (order.status === "completed" || order.status === "return") && availableProductsForReturn();

              return (
                <div className="order-history-row" key={order.orderNumber}>
                  <div className="order-history-details">
                    <div>
                      <div className="label">Order #</div>
                      <div className="value">{order.orderNumber}</div>
                    </div>
                    <div>
                      <div className="label">Status</div>
                      <div className={`status ${order.status.toLowerCase()}`}>
                        <span>{removeDash(order.status)}</span>
                      </div>
                    </div>
                    <div>
                      <div className="label">Date</div>
                      <div className="value">{convertDate(order.createdAt)}</div>
                    </div>
                  </div>

                  <div className="order-history-buttons">
                    <Button
                      className="order-history-link clear"
                      onClick={() => navigate("/support", { state: { orderNumber: order.orderNumber, previousUrl: "/account" } })}
                    >
                      Support
                    </Button>
                    <Button className="order-history-link clear" onClick={() => handleViewProducts(order)}>
                      View Details
                    </Button>
                    {order.status === "pending" && (
                      <Button className="order-history-link clear" onClick={() => handleShowCancelModal(order)}>
                        Cancel Order
                      </Button>
                    )}
                    {order.tracking && order.status === "shipped" && (
                      <a
                        className="order-history-link clear"
                        href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking}`}
                        target="_blank"
                      >
                        Track Package
                      </a>
                    )}
                    {showCreateReturnButton && (
                      <Button className="order-history-link clear" onClick={() => navigate("/return", { state: { order } })}>
                        Create Return
                      </Button>
                    )}
                    {(order.status === "return" || order.status === "refund") && (
                      <Button className="order-history-link clear" onClick={() => handleViewReturns(order)}>
                        View Returns
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <NoOrdersYet>
              <h1>No Orders Yet</h1>
              <ButtonLink to="/designs" className="autoWidth">
                Browse Designs
              </ButtonLink>
            </NoOrdersYet>
          )}
        </div>

        <Modal
          size={modalTemplate === "password" ? "md" : "lg"}
          fullscreen={"md-down"}
          show={showModal}
          animation={false}
          onHide={handleCloseModal}
        >
          {modalTemplate === "password" ? <PasswordTemplate /> : <ProductsTemplate order={currentOrder} />}
        </Modal>
        <Modal
          size={"lg"}
          fullscreen={"md-down"}
          show={showReturnsModal}
          onHide={() => setShowReturnsModal(false)}
          className="view-returns"
        >
          <ViewReturnsTemplate currentOrder={currentOrder} />
        </Modal>
        <Modal
          size={"sm"}
          fullscreen={"sm-down"}
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          className="view-cancel-modal"
        >
          <Wrapper className="modal-styles cancel-order">
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
              <PageTemplate>
                <div className="cancel-modal-content">
                  <span>Are you sure?</span>
                  <Button onClick={() => handleOrderCancellation()}>Cancel Order</Button>
                </div>
              </PageTemplate>
            </Modal.Body>
          </Wrapper>
        </Modal>
      </AccountTemplate>
    </>
  ) : (
    <>
      <HeaderTemplate className="narrow">
        <div className="header flex-header">
          <div className="title">
            <h1>Account Information</h1>
          </div>
          <div className="breadcrumb">
            <ButtonLink to="/designs" className="small clear">
              Browse Designs
            </ButtonLink>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="content-page">
          <VerifyEmail>
            <h2>Please verify your email to view account information</h2>
            <p>Check your email for a verification link</p>
            <Button className="logout lightgrey" onClick={logout}>
              Log out
            </Button>
          </VerifyEmail>
        </div>
      </PageTemplate>
    </>
  );
};

export default Customer;
